/**
 * Campaign "audience slice" — an optional ordering/position/reach-status
 * layer applied on top of a campaign's base targeting filter (all/segment/
 * tag/etc.). `value`s must match the backend's `CAMPAIGN_REACH_STATUS_KEYS`
 * in `contact-filter.service.ts` and the `audienceSlice` sub-schema in
 * `Campaign.model.ts` exactly — only the `label` copy here is meant to read
 * as plain, non-technical language for the person filling out the form.
 */
export const CAMPAIGN_REACH_STATUS_OPTIONS = [
  { value: 'any', label: 'Everyone matching the filters above' },
  // "Targeted" = a send was attempted (a CampaignLog row exists), regardless of
  // outcome — deliberately NOT worded as "received"/"reached", which implies the
  // message actually landed. A contact whose only attempt failed still counts as
  // "targeted": use the delivery-specific options below to filter by outcome.
  { value: 'neverTargeted', label: "Haven't been targeted by this campaign yet" },
  { value: 'targetedAny', label: 'Already targeted by this campaign (any outcome)' },
  { value: 'targetedPending', label: 'Sent — delivery still pending' },
  { value: 'targetedDelivered', label: 'Successfully delivered before' },
  { value: 'targetedNotDelivered', label: 'Sent, but never delivered' },
] as const;

/**
 * The form shows ONE plain-English "send order" choice instead of the raw
 * `sortBy` + `sortDir` pair the backend actually stores — e.g. "Newest
 * contacts first" reads far more naturally than picking "Date added" and
 * "Last" from two separate dropdowns. `sortBy`/`sortDir` here are the exact
 * values to send to the backend for that choice; split back out at
 * submit/populate time (see CampaignForm.tsx's `buildAudienceSliceValue` /
 * `populateFromCampaign`).
 */
export const AUDIENCE_SEND_ORDER_OPTIONS = [
  { value: 'dateAdded_asc', label: 'Oldest contacts first', sortBy: 'dateAdded', sortDir: 'asc' },
  { value: 'dateAdded_desc', label: 'Newest contacts first', sortBy: 'dateAdded', sortDir: 'desc' },
  {
    value: 'lastEngagement_desc',
    label: 'Most active contacts first',
    sortBy: 'lastEngagement',
    sortDir: 'desc',
  },
  {
    value: 'lastEngagement_asc',
    label: 'Least active contacts first',
    sortBy: 'lastEngagement',
    sortDir: 'asc',
  },
  { value: 'random', label: 'Random selection', sortBy: 'random', sortDir: 'asc' },
] as const;

export type AudienceSendOrder = (typeof AUDIENCE_SEND_ORDER_OPTIONS)[number]['value'];
